import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import SwiperCore, {
  Navigation,
  Pagination,
  A11y,
  Autoplay,
  Lazy,
  Keyboard,
} from 'swiper';
import { useIntersection } from 'app/hooks';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';

import { paginationTrack, navigationTrack } from './tracking';
import CarouselCard from './CarouselCard';
import styles from './Carousel.module.scss';
import './slides.scss';

SwiperCore.use([Keyboard]);

const PaginationContainer = () => {
  const swiper = useSwiper();
  const handlePaginationClick = (e) => {
    e.stopPropagation();
    const isPaginationBulletButton = e.target.classList.contains(
      'swiper-pagination-bullet'
    );

    if (isPaginationBulletButton) {
      const navigationDirection =
        swiper.previousIndex > swiper.activeIndex ? 'left' : 'right';
      paginationTrack(navigationDirection);
    }
  };

  return (
    <div
      onClick={handlePaginationClick}
      className="swiper-pagination"
    />
  );
};

function debounce(func, duration) {
  let timeout;
  return function debounceFunc(...args) {
    const effect = () => {
      timeout = null;
      return func.apply(this, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(effect, duration);
  };
}

const Carousel = () => {
  const carouselData = useSelector(
    (state) => state.creativeImageHomeData?.carouselAssets
  );
  const ref = useRef();
  const isCarouselVisible = carouselData.length ? useIntersection(ref) : false;
  const [slidesSeen, setSlidesSeen] = useState([]);

  const handleNavigationClick = (e) => {
    const navigationDirection = e.target.classList.contains(
      'swiper-button-prev'
    )
      ? 'left'
      : 'right';
    navigationTrack(navigationDirection);
  };

  const handleSlideViewTracking = (swiper, forceTracking = false) => {
    const slide = carouselData[swiper.realIndex];
    const seen = slidesSeen.includes(slide.id);
    if ((isCarouselVisible || forceTracking) && !seen) {
      setSlidesSeen([...slidesSeen, slide.id]);
    }
  };

  useEffect(() => {
    const handleScroll = debounce(() => {
      if (isCarouselVisible) {
        ref.current.swiper.autoplay.run();
      } else {
        ref.current.swiper.autoplay.pause();
      }
    }, 500);

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(handleScroll);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isCarouselVisible]);

  return carouselData.length ? (
    <Swiper
      autoplay={{
        delay: 6000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      }}
      centeredSlides
      lazy={{
        enabled: true,
        loadPrevNext: true,
      }}
      loop
      loopFillGroupWithBlank
      modules={[Navigation, Pagination, A11y, Autoplay, Lazy]}
      navigation={{
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next',
      }}
      pagination={{
        el: '.swiper-pagination',
        clickable: true,
      }}
      keyboard={{ enabled: true }}
      onAfterInit={(slide) => handleSlideViewTracking(slide, true)}
      onSlideChange={handleSlideViewTracking}
      slidesPerGroup={1}
      slidesPerView="auto"
      spaceBetween={10}
      watchSlidesProgress
      data-test-id="swiper-wrapper"
      ref={ref}
    >
      {Array.isArray(carouselData) &&
        carouselData.length &&
        carouselData.map(({ borderColor = null, ...card }) => (
          <SwiperSlide
            key={`${card.id}-${card.title}-${card.tracking}}`}
            className={cx({ [styles.cardBorder]: !!borderColor })}
            style={{ ...{ borderColor } }}
          >
            <CarouselCard
              title={card.title}
              subTitle={card.subTitle}
              ctaLabel={card.ctaLabel}
              ctaLink={card.ctaLink}
              backgroundImage={card.backgroundImage}
              backgroundVideo={card.backgroundVideo}
              headerImage={card.headerImage}
              titleImage={card.titleImage}
              theme={card.theme}
              tracking={card.tracking}
              openInNewTab={card.openInNewTab}
            />
          </SwiperSlide>
        ))}
      <button
        className="swiper-button-prev"
        type="button"
        tabIndex="0"
        aria-label="previous slide"
        onClick={handleNavigationClick}
      />
      <button
        className="swiper-button-next"
        tabIndex="0"
        type="button"
        aria-label="next slide"
        onClick={handleNavigationClick}
      />
      <PaginationContainer />
    </Swiper>
  ) : null;
};

export default Carousel;
